import {
  Box,
  Typography,
  Button,
  useMediaQuery,
  Dialog,
  DialogContent,
  IconButton
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import CloseIcon from '@mui/icons-material/Close';
import video from "../assets/video.mp4";
import theme from "../components/theme/Theme";
import { getVideoUrl } from "../apis/LaneCageApi";
import { HERO_VIDEO_RESUME_EVENT } from "../utils/heroVideo";

const Hero = () => {
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [videoUrl, setVideoUrl] = useState("");
  const [openModal, setOpenModal] = useState(false);
  const backgroundVideoRef = useRef<HTMLVideoElement>(null);

  const playBackgroundVideo = () => {
    const el = backgroundVideoRef.current;
    if (!el) return;
    el.muted = true;
    el.play().catch(() => {
      // Autoplay may still be blocked until a user gesture
    });
  };

  useEffect(() => {
    playBackgroundVideo();

    const onResume = () => playBackgroundVideo();
    window.addEventListener(HERO_VIDEO_RESUME_EVENT, onResume);

    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        playBackgroundVideo();
      }
    };
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      window.removeEventListener(HERO_VIDEO_RESUME_EVENT, onResume);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, []);

  const handleOpenVideoModal = async () => {
    try {
      const response = await getVideoUrl();
      setVideoUrl(response.url);
      setOpenModal(true);
    } catch (error: any) {
      console.error("Error fetching video URL:", error.message);
    }
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    playBackgroundVideo();
  };

  // Sticky header is navbar (80px); offer banner may add ~40px on homepage
  const headerOffset = isMobile ? "120px" : "80px";

  return (
    <Box
      sx={{
        minHeight: {
          xs: `calc(100dvh - ${headerOffset})`,
          md: `calc(100vh - ${headerOffset})`,
        },
        width: "100%",
        position: "relative",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        boxSizing: "border-box",
        overflow: "visible",
      }}
    >
      {/* Video Background */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          overflow: "hidden",
          zIndex: 0,
        }}
      >
        <video
          ref={backgroundVideoRef}
          src={video}
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            filter: "brightness(30%)",
          }}
          loop
          autoPlay
          muted
          playsInline
          preload="auto"
        />
      </Box>

      {/* Centered Content */}
      <Box
        sx={{
          position: "relative",
          zIndex: 1,
          width: "100%",
          maxWidth: "900px",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          textAlign: "center",
          color: "#fff",
          boxSizing: "border-box",
          px: { xs: 2.5, sm: 3, md: 4 },
          py: { xs: 3, md: 4 },
        }}
      >
        <Typography
          component="h1"
          sx={{
            fontWeight: "bold",
            fontSize: {
              xs: "1.75rem",
              sm: "2.25rem",
              md: "3.5rem",
            },
            lineHeight: 1.2,
            wordBreak: "break-word",
            px: { xs: 0.5, sm: 0 },
          }}
        >
          Welcome to The Bat Cage
        </Typography>
        <Typography
          sx={{
            opacity: 0.8,
            mt: { xs: 1.5, md: 2 },
            mb: { xs: 2.5, md: 3 },
            maxWidth: "700px",
            width: "100%",
            fontSize: {
              xs: "0.95rem",
              sm: "1.05rem",
              md: "1.25rem",
            },
            lineHeight: 1.5,
            px: { xs: 0.5, sm: 1 },
          }}
        >
          Your ultimate destination for batting practice and cricket training.
          Book premium lanes, join training sessions, and improve your game.
        </Typography>
        <Box
          sx={{
            display: "flex",
            gap: 2,
            flexWrap: "wrap",
            justifyContent: "center",
            width: "100%",
          }}
        >
          <Button
            variant="primary"
            onClick={() => navigate("/listings")}
          >
            Book a Session
          </Button>
          <Button
            variant="secondary"
            onClick={handleOpenVideoModal}
          >
            Take a Tour
          </Button>
        </Box>
      </Box>

      {/* Video Dialog/Modal */}
      <Dialog
        open={openModal}
        onClose={handleCloseModal}
        maxWidth="lg"
        fullWidth
      >
        <IconButton
          onClick={handleCloseModal}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: 'white',
            zIndex: 2,
            backgroundColor: 'rgba(0,0,0,0.3)',
            '&:hover': {
              backgroundColor: 'rgba(0,0,0,0.5)',
            }
          }}
        >
          <CloseIcon />
        </IconButton>
        <DialogContent sx={{ padding: 0, overflow: 'hidden', bgcolor: '#000' }}>
          {videoUrl && (
            <video
              src={videoUrl}
              style={{ width: '100%', height: 'auto' }}
              controls
              autoPlay
              playsInline
            />
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default Hero;